import React, { useCallback, useMemo, useState } from 'react'
import classnames from 'classnames'
import { skipToken } from '@reduxjs/toolkit/query'
import SaberCard from '../components/SaberCard'
import KpiStrip, { KpiDelta } from '../components/KpiStrip'
import SaberTable from '../components/SaberTable'
import type { SaberTableColumn } from '../components/SaberTable'
import KwhCell from '../components/KwhCell'
import MoneyCell from '../components/MoneyCell'
import PercentBarCell from '../components/PercentBarCell'
import { FilterField, MultiSelectControl } from '../components/Controls'
import { ArrowDownTrayIcon, CalendarIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import SaberControlBar, { SABER_TEAL_ACCENT } from '../components/SaberControlBar'
import { useStickyPanelOffset } from '../useStickyPanelOffset'
import {
  buildDashboardPeriodOptions,
  defaultDashboardPeriodKey,
  resolveDashboardPeriod,
  resolveDashboardPriorPeriod,
  isQuarterKey
} from './dashboard/dashboardPeriod'
import { useGetPortfolioCompletenessQuery } from '../saberApi'
import type { PortfolioCompletenessProperty } from '../saber.types'
import { previousMonthYm, useSaberSelection } from '../SaberContext'
import { date, integer, money, percent } from '../format'
import { CELL_COLOR, forecastBarClass, forecastPctColorClass } from '../cellColor'
import { resolvePropertyDisplay } from './detail/labels'
import StatusBadge from '../components/StatusBadge'
import NullDash from '../components/NullDash'
import DownloadDrawer from './dashboard/DownloadDrawer'

// Local widening of the v001 contract row: numeric fields are treated as
// nullable so ONBOARDING rows render safely (FOUNDATION_NOTES §5 — guard
// defensively at render time). `grossRevenue` is carried by the contract.
type DashboardRow = Omit<
  PortfolioCompletenessProperty,
  'forecastKwh' | 'billedKwh' | 'unbilledValue' | 'pctOfForecast' | 'billingLagDays' | 'grossRevenue'
> & {
  forecastKwh: number | null
  billedKwh: number | null
  unbilledValue: number | null
  pctOfForecast: number | null
  billingLagDays: number | null
  grossRevenue: number | null
}

// Group nullish/empty filter values under a single 'Unknown' bucket (§5).
const orUnknown = (value: string | null | undefined): string => (value == null || value === '' ? 'Unknown' : value)

const sum = (rows: DashboardRow[], pick: (row: DashboardRow) => number | null | undefined): number =>
  rows.reduce((acc, row) => acc + (pick(row) ?? 0), 0)

interface KpiTotals {
  gross: number
  unbilled: number
  pctBilled: number | null
  avgLagDays: number | null
}

// §5 KPI rollup over a set of OPERATING property rows. Shared by the current
// window and (WEB-6578) the prior-period comparison window so both use identical
// math and stay apples-to-apples.
const computeTotals = (operating: DashboardRow[]): KpiTotals => {
  const unbilled = sum(operating, (r) => r.unbilledValue)
  const gross = sum(operating, (r) => r.grossRevenue) + unbilled
  const forecastKwh = sum(operating, (r) => r.forecastKwh)
  const billedKwh = sum(operating, (r) => r.billedKwh)
  const pctBilled = forecastKwh > 0 ? (billedKwh / forecastKwh) * 100 : null
  const lags = operating.map((r) => r.billingLagDays).filter((v): v is number => v != null)
  const avgLagDays = lags.length > 0 ? lags.reduce((a, b) => a + b, 0) / lags.length : null
  return { gross, unbilled, pctBilled, avgLagDays }
}

// Relative % change vs prior; null when prior is absent or zero (avoids ÷0 / ∞).
const relDelta = (cur: number | null, prior: number | null): number | null =>
  cur == null || prior == null || prior === 0 ? null : ((cur - prior) / prior) * 100

// Percentage-point change vs prior; null when either side is absent.
const ppDelta = (cur: number | null, prior: number | null): number | null =>
  cur == null || prior == null ? null : cur - prior

// Property status filter options (raw values match PortfolioCompletenessProperty.status).
const STATUS_OPTIONS: ReadonlyArray<DashboardRow['status']> = ['OPERATING', 'ONBOARDING']

// Local status pill — the shared StatusBadge (frozen) maps neither OPERATING
// nor ONBOARDING to the spec's green/gray colors, so render an equivalent pill
// here using the same class conventions.
const PILL_CLASSES: Record<DashboardRow['status'], string> = {
  OPERATING: 'bg-green-100 dark:bg-green-400/10 text-green-800 dark:text-green-400 ring-green-600/40 dark:ring-green-400/20',
  ONBOARDING: 'bg-gray-50 dark:bg-gray-400/10 text-gray-800 dark:text-gray-400 ring-gray-600/10 dark:ring-gray-400/20'
}

const STATUS_TOOLTIPS: Record<DashboardRow['status'], string> = {
  OPERATING: 'Has measured allocated kWh in the selected range.',
  ONBOARDING: 'A property SABER knows about but with zero measured allocated kWh in the range yet.'
}

const StatusPill: React.FC<{ status: DashboardRow['status'] }> = ({ status }) => (
  <span
    title={STATUS_TOOLTIPS[status]}
    className={classnames(
      'inline-flex cursor-help items-center rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset',
      PILL_CLASSES[status]
    )}
  >
    {status === 'OPERATING' ? 'Operating' : 'Onboarding'}
  </span>
)

export interface DashboardProps {
  /**
   * v001 — open the Detail tab for a property. WEB-6567: the layout turns this
   * into a single navigation to /saber/detail?property=&month=, so the
   * selection is one history entry and the URL deep-links to it.
   */
  onOpenDetail?: (propertyCuid: string, month: string) => void
}

export const Dashboard: React.FC<DashboardProps> = ({ onOpenDetail }) => {
  // WEB-6581 — the period control now mirrors Shadow Billing's chip bar, with a
  // Dashboard choice set (2 quarters + 4 months + Custom). `period` is a key from
  // buildDashboardPeriodOptions; `customRange` backs the Custom dual-calendar.
  // Default to the current month. Skip the query while Custom is incomplete.
  const today = useMemo(() => new Date(), [])
  const periodOptions = useMemo(() => buildDashboardPeriodOptions(today), [today])
  // WEB-6581: the period + Custom range live in SaberContext (URL-backed) so the
  // selection persists when switching to the Shadow Billing tab. Fall back to the
  // current-month default until the user picks a chip.
  const { period: ctxPeriod, setPeriod, customRange, setCustomRange } = useSaberSelection()
  const period = ctxPeriod ?? defaultDashboardPeriodKey(today)
  const resolved = useMemo(() => resolveDashboardPeriod(period, customRange), [period, customRange])
  const { data, isLoading, isError, refetch } = useGetPortfolioCompletenessQuery(resolved ?? skipToken)

  // Entering Custom seeds the editable range from whatever window is resolved now
  // (in the same URL write — see setPeriod's contract in SaberContext).
  const onPeriodChange = (key: string): void => {
    if (key === 'custom' && customRange == null && resolved != null) setPeriod(key, resolved)
    else setPeriod(key)
  }

  const allRows: DashboardRow[] = useMemo(() => data?.properties ?? [], [data?.properties])

  // §5 filters — Utility + Investor + Status, with nulls grouped as 'Unknown'.
  // null selection = untouched = all values.
  const propertyNames = useMemo(
    () => Array.from(new Set(allRows.map((p) => resolvePropertyDisplay(p.propertyName, p.location).name))).sort(),
    [allRows]
  )
  const utilities = useMemo(() => Array.from(new Set(allRows.map((p) => orUnknown(p.utility)))).sort(), [allRows])
  const investors = useMemo(() => Array.from(new Set(allRows.map((p) => orUnknown(p.investor)))).sort(), [allRows])
  const statuses = useMemo(() => STATUS_OPTIONS.filter((s) => allRows.some((p) => p.status === s)), [allRows])
  const [selectedProperties, setSelectedProperties] = useState<string[] | null>(null)
  const [selectedUtilities, setSelectedUtilities] = useState<string[] | null>(null)
  const [selectedInvestors, setSelectedInvestors] = useState<string[] | null>(null)
  const [selectedStatuses, setSelectedStatuses] = useState<string[] | null>(null)
  const [propertySearch, setPropertySearch] = useState('')
  const [downloadOpen, setDownloadOpen] = useState(false)
  const closeDownload = useCallback(() => setDownloadOpen(false), [])
  const activeProperties = selectedProperties ?? propertyNames
  const activeUtilities = selectedUtilities ?? utilities
  const activeInvestors = selectedInvestors ?? investors
  const activeStatuses = selectedStatuses ?? statuses
  const propertiesFiltered = selectedProperties != null && activeProperties.length !== propertyNames.length
  const utilitiesFiltered = selectedUtilities != null && activeUtilities.length !== utilities.length
  const investorsFiltered = selectedInvestors != null && activeInvestors.length !== investors.length
  const statusesFiltered = selectedStatuses != null && activeStatuses.length !== statuses.length
  const propertySearchActive = propertySearch.trim() !== ''
  const filtersActive =
    propertySearchActive || propertiesFiltered || utilitiesFiltered || investorsFiltered || statusesFiltered

  const matchesFilters = useCallback(
    (p: DashboardRow): boolean =>
      activeProperties.includes(resolvePropertyDisplay(p.propertyName, p.location).name) &&
      activeUtilities.includes(orUnknown(p.utility)) &&
      activeInvestors.includes(orUnknown(p.investor)) &&
      activeStatuses.includes(p.status),
    [activeProperties, activeUtilities, activeInvestors, activeStatuses]
  )

  const rows = useMemo(() => allRows.filter(matchesFilters), [allRows, matchesFilters])

  const matchesPropertySearch = useCallback(
    (p: DashboardRow): boolean => {
      const q = propertySearch.trim().toLowerCase()
      if (q === '') return true
      const { name, location } = resolvePropertyDisplay(p.propertyName, p.location)
      return name.toLowerCase().includes(q) || (location?.toLowerCase().includes(q) ?? false)
    },
    [propertySearch]
  )

  const tableRows = useMemo(() => rows.filter(matchesPropertySearch), [rows, matchesPropertySearch])

  // §5 — `act` = filtered operating rows; all KPIs + footer totals derive from it.
  const act = useMemo(() => rows.filter((p) => p.status === 'OPERATING'), [rows])
  const totals = useMemo(() => computeTotals(act), [act])

  // WEB-6578 — period-over-period deltas. Resolve the prior comparison window
  // (Month → prior month, Quarter → prior quarter; null for Custom), fetch it from
  // the SAME endpoint, and recompute the KPIs over the identically filtered
  // operating rows. Omitted entirely when there is no prior-period data.
  const priorResolved = useMemo(() => resolveDashboardPriorPeriod(period), [period])
  const { data: priorData } = useGetPortfolioCompletenessQuery(priorResolved ?? skipToken)
  const priorAct = useMemo<DashboardRow[]>(
    () => (priorData?.properties ?? []).filter((p) => p.status === 'OPERATING' && matchesFilters(p)),
    [priorData?.properties, matchesFilters]
  )
  const deltas = useMemo(() => {
    if (priorResolved == null || priorAct.length === 0) return null
    const prior = computeTotals(priorAct)
    return {
      gross: relDelta(totals.gross, prior.gross),
      pctBilled: ppDelta(totals.pctBilled, prior.pctBilled),
      unbilled: relDelta(totals.unbilled, prior.unbilled),
      avgLag: relDelta(totals.avgLagDays, prior.avgLagDays)
    }
  }, [priorResolved, priorAct, totals])
  const deltaTitle = isQuarterKey(period) ? 'vs prior quarter' : 'vs prior month'

  const hasData = data != null

  // v001 — clicking an OPERATING row selects that property, requests the
  // previous month as the Detail period, and opens the Detail tab.
  // ONBOARDING rows are inert.
  const handleRowClick = (row: DashboardRow): void => {
    if (row.status !== 'OPERATING') return
    onOpenDetail?.(row.propertyCuid, previousMonthYm())
  }

  const clearFilters = (): void => {
    setPropertySearch('')
    setSelectedProperties(null)
    setSelectedUtilities(null)
    setSelectedInvestors(null)
    setSelectedStatuses(null)
  }

  const columns: Array<SaberTableColumn<DashboardRow>> = useMemo(
    () => [
      {
        key: 'property',
        header: 'Property',
        sortable: true,
        sortValue: (r) => resolvePropertyDisplay(r.propertyName, r.location).name,
        render: (r) => {
          const { name, location } = resolvePropertyDisplay(r.propertyName, r.location)
          return (
            <div>
              <div className={classnames('font-medium', CELL_COLOR.primary)}>{name}</div>
              {location != null && (
                <div className={classnames('text-xs', CELL_COLOR.secondary)}>{location}</div>
              )}
            </div>
          )
        }
      },
      {
        key: 'utility',
        header: 'Utility',
        sortable: true,
        sortValue: (r) => orUnknown(r.utility),
        render: (r) => <StatusBadge status={orUnknown(r.utility)} palette="utility" />
      },
      {
        key: 'investor',
        header: 'Investor',
        sortable: true,
        sortValue: (r) => orUnknown(r.investor),
        render: (r) => <StatusBadge status={orUnknown(r.investor)} />
      },
      {
        key: 'capacity',
        header: 'Capacity (kW)',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.capacityKw,
        // capacityKw has no source today — always renders an em dash.
        render: (r) => integer(r.capacityKw)
      },
      {
        key: 'billed',
        header: 'Billed',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.billedKwh,
        render: (r) => <KwhCell value={r.billedKwh} />
      },
      {
        key: 'unbilled',
        header: 'Unbilled',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.unbilledValue,
        render: (r) => (
          <MoneyCell value={r.unbilledValue} role="loss" />
        )
      },
      {
        key: 'pctOfForecast',
        header: '% of Forecast',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.pctOfForecast,
        render: (r) =>
          r.pctOfForecast == null ? (
            <NullDash />
          ) : (
            <PercentBarCell
              value={r.pctOfForecast}
              valueClassName={classnames('font-medium', forecastPctColorClass(r.pctOfForecast))}
              barClassName={forecastBarClass(r.pctOfForecast)}
              ariaDescription="of forecast"
            />
          )
      },
      {
        key: 'status',
        header: 'Status',
        sortable: true,
        sortValue: (r) => r.status,
        render: (r) => <StatusPill status={r.status} />
      }
    ],
    []
  )

  const { pageHeaderRef, panelStickyProps } = useStickyPanelOffset()

  return (
    // WEB-6581: drop the screen-level top padding (the SABER layout already pads);
    // pb-4 keeps a little breathing room at the bottom.
    <div className="space-y-4 pb-4">
      {/* WEB-6581: title with subtitle inline on the left; resolved window on the row below. */}
      <div
        ref={pageHeaderRef}
        className="sticky top-0 z-10 -mx-4 space-y-1 border-b border-slate-200 bg-white px-4 py-2.5 dark:border-slate-700 dark:bg-slate-900 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8"
      >
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
          <div className="space-y-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <h2 className="m-0 text-lg font-semibold text-slate-900 dark:text-slate-100">Dashboard</h2>
              <p className="m-0 text-xs text-slate-500 dark:text-slate-400">Billing completeness across all properties</p>
            </div>
            {resolved != null && (
              <span className={classnames('inline-flex items-center gap-1 text-sm font-medium', SABER_TEAL_ACCENT)}>
                <CalendarIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                {date(resolved.from)} – {date(resolved.to)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => setDownloadOpen(true)}
            aria-expanded={downloadOpen}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Download
          </button>
        </div>

        {/* WEB-6581: period chips only — property filters live in the Properties card. */}
        <SaberControlBar
          periods={periodOptions}
          period={period}
          onPeriodChange={onPeriodChange}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
          customRangeTestId="dashboard-custom-range-picker"
        />
      </div>

      {downloadOpen && (
        <DownloadDrawer
          properties={allRows}
          seedPeriod={period}
          seedCustomRange={customRange}
          onClose={closeDownload}
        />
      )}

      <KpiStrip
        className="lg:grid-cols-5"
        items={[
          {
            label: 'Operating Properties',
            value: hasData ? act.length : '—',
            tooltip:
              'Number of operating properties included in the portfolio totals, after applying the current filters.',
          },
          {
            label: 'Gross Revenue',
            value: hasData ? money(totals.gross) : '—',
            valueClassName: hasData ? CELL_COLOR.positive : undefined,
            tooltip:
              'Total solar revenue across operating properties this period — billed gross plus shadow-billed (unbilled).',
            // Higher revenue is better.
            delta:
              deltas?.gross != null ? (
                <KpiDelta value={deltas.gross} unit="pct" higherIsBetter title={deltaTitle} />
              ) : undefined
          },
          {
            label: '% Billed of Forecast',
            value: hasData ? percent(totals.pctBilled) : '—',
            tooltip:
              'Total billed kWh ÷ total forecast generation across operating properties, as a percent.',
            // A percentage metric → percentage-point delta; higher is better.
            delta:
              deltas?.pctBilled != null ? (
                <KpiDelta value={deltas.pctBilled} unit="pp" higherIsBetter title={deltaTitle} />
              ) : undefined
          },
          {
            label: 'Unbilled $',
            value: hasData ? money(totals.unbilled) : '—',
            valueClassName:
              hasData && totals.unbilled > 0 ? CELL_COLOR.negative : undefined,
            tooltip:
              'Solar revenue SABER has shadow-billed but the utility has not yet invoiced, summed across operating properties.',
            // Lower unbilled is better.
            delta:
              deltas?.unbilled != null ? (
                <KpiDelta value={deltas.unbilled} unit="pct" higherIsBetter={false} title={deltaTitle} />
              ) : undefined
          },
          {
            label: 'Avg Days to Close',
            value: hasData && totals.avgLagDays != null ? `${totals.avgLagDays.toFixed(1)} days` : '—',
            tooltip:
              'Average billing lag — mean days to close a bill — across operating properties that have lag data.',
            // Lower billing lag is better (WEB-6578: prototype's ▲ -8% was a mock error).
            delta:
              deltas?.avgLag != null ? (
                <KpiDelta value={deltas.avgLag} unit="pct" higherIsBetter={false} title={deltaTitle} />
              ) : undefined
          }
        ]}
      />

      <SaberCard title="Properties" {...panelStickyProps(1)}>
        {isError ? (
          <div className="flex flex-col items-start gap-2 py-6 text-sm text-red-600 dark:text-red-400">
            <span>Failed to load portfolio completeness.</span>
            <button
              type="button"
              onClick={() => {
                refetch().catch(() => undefined)
              }}
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Retry
            </button>
          </div>
        ) : isLoading ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">Loading…</div>
        ) : !hasData ? (
          <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
            {resolved == null ? 'Pick a complete date range to load data.' : 'Loading…'}
          </div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap items-end gap-4">
              <FilterField label="Properties" className="min-w-[16rem] max-w-sm flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500"
                    aria-hidden
                  />
                  <input
                    type="search"
                    value={propertySearch}
                    onChange={(e) => setPropertySearch(e.target.value)}
                    placeholder="Filter properties…"
                    aria-label="Filter properties"
                    data-testid="dashboard-property-search"
                    className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-9 pr-2.5 text-xs font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
                  />
                </div>
              </FilterField>
              <div className="flex flex-wrap items-end gap-4" role="group" aria-label="Filters">
                <FilterField label="Utility">
                  <MultiSelectControl
                    id="dashboard-utility"
                    label="Utility"
                    options={utilities}
                    selected={activeUtilities}
                    onChange={setSelectedUtilities}
                    field
                    allSummary="All utilities"
                    noneSummary="No utilities"
                  />
                </FilterField>
                <FilterField label="Investor">
                  <MultiSelectControl
                    id="dashboard-investor"
                    label="Investor"
                    options={investors}
                    selected={activeInvestors}
                    onChange={setSelectedInvestors}
                    field
                    allSummary="All investors"
                    noneSummary="No investors"
                  />
                </FilterField>
                <FilterField label="Status">
                  <MultiSelectControl
                    id="dashboard-status"
                    label="Status"
                    options={statuses}
                    selected={activeStatuses}
                    onChange={setSelectedStatuses}
                    field
                    allSummary="All statuses"
                    noneSummary="No statuses"
                  />
                </FilterField>
                {filtersActive && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="mb-0 rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700/60"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
            <SaberTable
              columns={columns}
              rows={tableRows}
              rowKey={(r) => r.propertyCuid}
              onRowClick={handleRowClick}
              rowClassName={(r) => (r.status === 'ONBOARDING' ? 'opacity-60 !cursor-default' : undefined)}
              emptyMessage={
                propertySearchActive
                  ? 'No properties match your search.'
                  : filtersActive
                    ? 'No properties match the current filters.'
                    : 'No properties in this range.'
              }
              helpKey="dashboard"
            />
          </>
        )}
      </SaberCard>
    </div>
  )
}

export default Dashboard
